import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Typography, Box, Stack, Chip, Divider, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import { GLOSSARY, IMPROVE_TIPS, OVERVIEW_EXPLAINER } from './backtestHelp';

const groups = ['Core', 'Performance', 'Risk', 'Quality', 'Validation', 'Costs', 'Charts'];

const BacktestGuide: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <MenuBookIcon color="primary" /> Backtest Studio — User Guide
      <Box sx={{ flex: 1 }} />
      <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
    </DialogTitle>
    <DialogContent dividers>
      <Typography variant="h6" fontWeight={800} gutterBottom>What is a backtest?</Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        A backtest replays a trading rule over historical price data to estimate how it <i>would</i> have performed — how much
        it makes per trade, how often it wins, how deep the losses get, and how steady the equity curve is. It is a research
        tool, not a guarantee: real trading has costs, slippage and changing markets. This Studio applies professional best
        practices automatically so even a first-time user gets a sound test:
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {['No look-ahead / no repaint', 'Realistic fees + slippage', 'Out-of-sample tested', 'Robustness-ranked (not the single best cell)', 'Benchmarked vs buy & hold'].map(t =>
          <Chip key={t} size="small" label={t} color="success" variant="outlined" />)}
      </Stack>

      <Typography variant="h6" fontWeight={800} gutterBottom>How the strategy reads the market</Typography>
      <Typography variant="body2" color="text.secondary" paragraph>{OVERVIEW_EXPLAINER}</Typography>

      <Typography variant="h6" fontWeight={800} gutterBottom>How to read the report (top to bottom)</Typography>
      <Box component="ol" sx={{ pl: 2.5, mb: 2 }}>
        {[
          ['Recommended + “Why #1”', 'The best rule for that timeframe and the plain-language reason it ranked first.'],
          ['Trading rules', 'Exactly when the chosen rule enters and exits — so you know what you’re evaluating.'],
          ['Scorecard', 'The headline numbers (expectancy, profit factor, win rate, drawdown, Sharpe…). Hover any tile for its meaning.'],
          ['Equity curve & drawdown', 'The account value over time vs buy & hold, and how deep the dips got.'],
          ['Per-symbol & monthly', 'Which symbols/months drove the result — spot winners, laggards and rough patches.'],
          ['Out-of-sample', 'Did it hold up on unseen data? A big drop means likely overfit.'],
          ['Trades (per symbol)', 'Every trade with entry/exit details — click the chart icon to inspect it on the chart.'],
          ['All combinations', 'Every parameter set tested, ranked. Click a row to load its charts.'],
        ].map(([t, d]) => <li key={t}><Typography variant="body2"><b>{t}</b> — <span style={{ opacity: 0.8 }}>{d}</span></Typography></li>)}
      </Box>

      <Typography variant="h6" fontWeight={800} gutterBottom>Glossary — every term explained</Typography>
      {groups.map(g => (
        <Accordion key={g} disableGutters defaultExpanded={g === 'Performance'} sx={{ '&:before': { display: 'none' }, mb: 0.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}><Typography fontWeight={700}>{g}</Typography></AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {GLOSSARY.filter(i => i.group === g).map(i => (
                <Box key={i.term}>
                  <Typography variant="body2" fontWeight={700} color="primary">{i.term}</Typography>
                  <Typography variant="body2" color="text.secondary">{i.text}</Typography>
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}

      <Divider sx={{ my: 2 }} />
      <Typography variant="h6" fontWeight={800} gutterBottom>How to improve your results</Typography>
      <Box component="ul" sx={{ pl: 2.5 }}>
        {IMPROVE_TIPS.map((t, i) => <li key={i}><Typography variant="body2" color="text.secondary">{t}</Typography></li>)}
      </Box>
    </DialogContent>
  </Dialog>
);

export default BacktestGuide;
